import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { AppConfig } from '../app.config';
import { GlobalPopupService } from '../components/generic-popup/presets/global-popup/global-popup.service';
import { WikiManualService } from '../pages/wiki/wiki-manual/wiki-manual.service';
import { WikiManualPage } from '../pages/wiki/wiki-manual/wiki-manual.models';

/**
 * Shows a one-time "new version" notice after the app is upgraded.
 *
 * The last version the user acknowledged is kept in localStorage. On login we compare it with
 * AppConfig.APPVersion (stamped from the git tag into assets/version.json at build time); a
 * difference means this browser has not seen the current release yet.
 *
 * Storing the version string rather than a boolean is what makes this self-managing: every
 * release invalidates the stored value exactly once, with no per-release flag to clean up.
 *
 * ponytail: localStorage, not a cookie — the value is never needed server-side, and a cookie
 * would be sent on every request for nothing. Scope is therefore per browser profile, so the
 * same person on Chrome and Edge sees the notice twice. Upgrade path if that matters: a
 * per-user column behind the existing user API instead of this key.
 */

const RELEASE_NOTES_PREFIX = '07-release-notes/';

@Injectable({ providedIn: 'root' })
export class ReleaseNoticeService {
  // getUserDetails() runs from four setLoginDisplay() call sites with no equivalent of the
  // loginHandled guard, and the version check and the write are separated by an async manifest
  // fetch — so without this two invocations both pass the check and both show the popup.
  private checked = false;

  constructor(
    private globalPopupService: GlobalPopupService,
    private wikiManualService: WikiManualService
  ) {}

  checkForNewRelease(): void {
    if (this.checked) return;
    this.checked = true;

    // This runs inside getUserDetails()'s try block, whose catch reports *any* throw as
    // "There was a problem with signing into your account". localStorage throws in Safari
    // private mode, with site data blocked, or on quota — and the user would then be logged
    // in and told login failed. A release notice must never be able to affect the login path.
    try {
      this.run();
    } catch (error) {
      console.warn('ReleaseNoticeService: skipped', error);
    }
  }

  private run(): void {
    const current = AppConfig.APPVersion;

    // Compiled-in fallback, meaning assets/version.json failed to load or timed out
    // (version.initializer.ts swallows both). Announcing it would show "updated to version
    // 0.0.0-dev" and overwrite the real stored version, firing a second popup on the next
    // good load.
    if (current === '0.0.0-dev') return;

    const lastSeen = localStorage.getItem(AppConfig.ReleaseNoticeKey);

    // First ever load on this browser: seed silently. A brand new user should not be greeted
    // with "what's new in 3.7.0" for a release they have never used.
    if (lastSeen === null) {
      localStorage.setItem(AppConfig.ReleaseNoticeKey, current);
      return;
    }

    if (lastSeen === current) return;

    // Manifest is shareReplay(1) in WikiManualService, so this is cached after the first wiki visit.
    this.wikiManualService
      .getManifest()
      .pipe(take(1))
      .subscribe({
        next: manifest => this.show(current, this.findReleaseNotesPage(manifest.pages, current)),
        // Never let a missing/broken manifest swallow the notice — show it without the link.
        error: () => this.show(current, undefined),
      });
  }

  /**
   * Manifest ids for release notes look like `07-release-notes/v3-6-0`. Prefer the page for the
   * exact version; fall back to the newest release-notes page so the link is never dead if a
   * release ships without its own note.
   *
   * `notes[0]` is the newest: release-notes.mjs registers each release at order 0 and rebuilds
   * `pages` in tree order, so the section reads newest-first.
   */
  private findReleaseNotesPage(pages: WikiManualPage[], version: string): string | undefined {
    const notes = pages.filter(p => p.id.startsWith(RELEASE_NOTES_PREFIX));
    if (notes.length === 0) return undefined;
    const exactId = `${RELEASE_NOTES_PREFIX}v${version.replace(/\./g, '-')}`;
    return notes.some(p => p.id === exactId) ? exactId : notes[0].id;
  }

  private show(version: string, pageId: string | undefined): void {
    // Marked as seen when the popup is displayed, not when Ok is clicked: the generic popup can
    // also be dismissed with its X or by clicking outside, and in every one of those cases the
    // user has had their notice. Writing here means it cannot be shown twice.
    localStorage.setItem(AppConfig.ReleaseNoticeKey, version);

    const linkText = $localize`:@@newReleaseNotesLink:View release notes`;
    const link = pageId
      ? `<br><br><a href="/wiki/manual?page=${encodeURIComponent(pageId)}">${linkText}</a>`
      : '';

    this.globalPopupService.showPopup({
      title: $localize`:@@newReleaseTitle:New version available`,
      content:
        $localize`:@@newReleaseText:gpd.ONE has been updated to version ${version}:VERSION:.` + link,
      buttons: [{ text: $localize`:@@ok:Ok`, onClick: () => {} }],
    });
  }
}
